import { Checkbox, FormControlLabel, Stack, Typography, Slider, TextField, Button, Box, Tab, Tabs, useTheme, MenuItem } from '@mui/material';
import { observer } from 'mobx-lite';
import { MainStoreContextItem } from '../../Stores/MainStore';
import { useState } from 'react';
import {
    ContentCopy, Check, Refresh,
    VisibilityOutlined, ShareOutlined,
    Lock, Badge, FaceRetouchingNatural,
    Flip, BugReport, QueryStats, Highlight,
    Timer, AvTimer, Palette, Stars, PhotoSizeSelectSmall,
    ThumbUpAlt, CenterFocusStrong, ZoomOutMap,
    Videocam,
} from '@mui/icons-material';

import Color from 'color';

/* ── Shared helpers ──────────────────────────────────────────── */

const tabIconBadge = (accentColor: string) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${Color(accentColor).alpha(0.2).hexa()}, ${Color(accentColor).alpha(0.05).hexa()})`,
    border: `1px solid ${Color(accentColor).alpha(0.25).hexa()}`,
    flexShrink: 0, 
});

const inputSx = (accentColor: string) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${Color(accentColor).alpha(0.15).hexa()}`,
        },
    },
});

const sliderGroupSx = (accentColor: string) => ({
    mx: 0,
    px: 2,
    py: 1,
    borderRadius: 2,
    border: '1px solid',
    borderColor: Color(accentColor).alpha(0.3).hexa(),
    backgroundColor: Color(accentColor).alpha(0.04).hexa(),
    transition: 'all 0.2s ease',
    '&:hover': { borderColor: Color(accentColor).alpha(0.4).hexa(), backgroundColor: Color(accentColor).alpha(0.03).hexa() },
});

interface SliderRowProps {
    label: string;
    value: string;
    caption: string;
    icon?: React.ReactNode;
    accentColor: string;
    sliderProps: React.ComponentProps<typeof Slider>;
}

const SliderRow: React.FC<SliderRowProps> = ({ label, value, caption, icon, accentColor, sliderProps }) => (
    <Box sx={sliderGroupSx(accentColor)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Typography variant="body2" fontWeight={500}>{label}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>{value}</Typography>
        </Box>
        <Slider
            {...sliderProps}
            sx={{
                mx: 2.5,
                width: 'calc(100% - 40px)',
                '& .MuiSlider-markLabel': {
                    whiteSpace: 'nowrap',
                },
                ...sliderProps.sx,
            }}
            valueLabelDisplay="auto"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {caption}
        </Typography>
    </Box>
);

/* ── Tab panels ──────────────────────────────────────────────── */

interface TabPanelProps { children: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2.5 }}>
        {value === index && children}
    </Box>
);

/* ── Main component ──────────────────────────────────────────── */

const GeneralSettings: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const { settingsStore, mediaDeviceStore } = store;
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [tab, setTab] = useState(0);
    const muiTheme = useTheme();
    const accent = muiTheme.palette.primary.main;

    const handleGenerateShareableUrl = () => {
        const shareableUrl = settingsStore.generateConfigUrl(undefined, store.eventThemesStore);
        navigator.clipboard.writeText(shareableUrl).then(() => {
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 2000);
        });
    };

    const handleResetToDefaults = () => {
        if (confirm('Are you sure you want to reset all settings to their default values? This action cannot be undone.')) {
            settingsStore.resetToDefaults();
            store.eventThemesStore.resetToDefaults();
        }
    };

    const btnContainedSx = {
        borderRadius: 2,
        textTransform: 'none' as const,
        fontWeight: 600,
        px: 2.5,
        boxShadow: `0 2px 12px ${Color(accent).alpha(0.3).hexa()}`,
        '&:hover': { boxShadow: `0 4px 20px ${Color(accent).alpha(0.4).hexa()}` },
    };

    const btnOutlinedSx = {
        borderRadius: 2,
        textTransform: 'none' as const,
        fontWeight: 500,
        px: 2.5,
        borderColor: Color('#fff').alpha(0.15).hexa(),
        '&:hover': { borderColor: Color('#fff').alpha(0.3).hexa(), backgroundColor: Color('#fff').alpha(0.04).hexa() },
    };

    return (
        <Stack spacing={0}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>

                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                        General Settings
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Display, detection tuning & configuration sharing
                    </Typography>
                </Box>
            </Box>

            {/* Section tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    minHeight: 40,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        minHeight: 40,
                        fontSize: '0.85rem',
                    },
                }}
            >
                <Tab icon={<Box sx={tabIconBadge(accent)}><VisibilityOutlined sx={{ fontSize: 16, color: accent }} /></Box>} iconPosition="start" label="Display" />
                <Tab icon={<Box sx={tabIconBadge(accent)}><FaceRetouchingNatural sx={{ fontSize: 16, color: accent }} /></Box>} iconPosition="start" label="Detection" />
                <Tab icon={<Box sx={tabIconBadge(accent)}><ShareOutlined sx={{ fontSize: 16, color: accent }} /></Box>} iconPosition="start" label="Sharing" />
            </Tabs>

            {/* ─── Display Tab ─── */}
            <TabPanel value={tab} index={0}>
                <Stack spacing={2.5}>
                    {/* Auth PIN – card style */}
                    <Box
                        sx={{
                            mx: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: settingsStore.authPin ? Color(accent).alpha(0.3).hexa() : 'divider',
                            backgroundColor: settingsStore.authPin ? Color(accent).alpha(0.04).hexa() : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Lock fontSize="small" sx={{ color: accent, ml: 5 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>Authentication PIN</Typography>
                            <Typography variant="caption" color="text.secondary">PIN required to access protected settings and routes</Typography>
                        </Box>
                        <TextField
                            value={settingsStore.authPin}
                            onChange={(event) => {
                                const value = event.target.value.replace(/\D/g, '');
                                settingsStore.setAuthPin(value);
                            }}
                            placeholder="PIN"
                            type="password"
                            inputMode="numeric"
                            slotProps={{
                                htmlInput: { pattern: '[0-9]*', maxLength: 10, style: { letterSpacing: '0.2em', textAlign: 'center' } },
                            }}
                            size="small"
                            sx={{ width: 120, ...inputSx(accent) }}
                        />
                    </Box>

                    {/* Camera selection – card style */}
                    <Box
                        sx={{
                            mx: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: settingsStore.preferredCameraLabel ? Color(accent).alpha(0.3).hexa() : 'divider',
                            backgroundColor: settingsStore.preferredCameraLabel ? Color(accent).alpha(0.04).hexa() : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Videocam fontSize="small" sx={{ color: accent, ml: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>Camera</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {mediaDeviceStore.loading
                                    ? 'Detecting cameras…'
                                    : mediaDeviceStore.devices.length === 0
                                        ? 'No cameras detected.'
                                        : 'Choose which camera to use. "Auto" picks the first available.'}
                            </Typography>
                        </Box>
                        <TextField
                            select
                            value={settingsStore.preferredCameraLabel ?? ''}
                            onChange={(event) => {
                                const label = event.target.value || null;
                                settingsStore.setPreferredCameraLabel(label);
                                const target = label
                                    ? mediaDeviceStore.devices.find((d) => d.label === label)
                                    : mediaDeviceStore.devices[0];
                                if (target) mediaDeviceStore.setSelectedDeviceId(target.deviceId);
                            }}
                            size="small"
                            disabled={mediaDeviceStore.devices.length === 0}
                            sx={{ width: 240, ...inputSx(accent) }}
                        >
                            <MenuItem value="">Auto (first available)</MenuItem>
                            {mediaDeviceStore.devices
                                .filter((d) => d.label !== '')
                                .map((device) => (
                                    <MenuItem key={device.deviceId} value={device.label}>
                                        {device.label}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </Box>

                    {[
                        { checked: settingsStore.flipVideoHorizontally, onChange: (v: boolean) => settingsStore.setFlipVideoHorizontally(v), title: 'Flip Video Horizontally', desc: 'Mirror the video feed horizontally (useful for selfie-style viewing)', icon: <Flip fontSize="small" sx={{ color: accent }} /> },
                        { checked: settingsStore.showDebugLog, onChange: (v: boolean) => settingsStore.setShowDebugLog(v), title: 'Debug Log', desc: 'Show a debug log panel at the bottom of the screen with diagnostic messages', icon: <BugReport fontSize="small" sx={{ color: accent }} /> },
                        { checked: settingsStore.showNerdStatistics, onChange: (v: boolean) => settingsStore.setShowNerdStatistics(v), title: 'Nerd Statistics', desc: 'Show face detection rectangles and detailed system status', icon: <QueryStats fontSize="small" sx={{ color: accent }} /> },
                        { checked: settingsStore.faceSpotlight, onChange: (v: boolean) => settingsStore.setFaceSpotlight(v), title: 'Face Spotlight', desc: 'Dim the background and highlight the detected face with a spotlight effect', icon: <Highlight fontSize="small" sx={{ color: accent }} /> },
                    ].map(({ checked, onChange, title, desc, icon }) => (
                        <FormControlLabel
                            key={title}
                            control={
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => onChange(e.target.checked)}
                                    sx={{ '&.Mui-checked': { color: accent } }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {icon}
                                    <Box>
                                        <Typography variant="body1" fontWeight={500}>{title}</Typography>
                                        <Typography variant="caption" color="text.secondary">{desc}</Typography>
                                    </Box>
                                </Box>
                            }
                            sx={{
                                mx: 0,
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: checked ? Color(accent).alpha(0.3).hexa() : 'divider',
                                backgroundColor: checked ? Color(accent).alpha(0.04).hexa() : 'transparent',
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                            }}
                        />
                    ))}
                </Stack>
            </TabPanel>

            {/* ─── Detection Tab ─── */}
            <TabPanel value={tab} index={1}>
                <Stack spacing={2}>
                    {/* Detector Name – card style */}
                    <Box
                        sx={{
                            mx: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: settingsStore.detectorName ? Color(accent).alpha(0.3).hexa() : 'divider',
                            backgroundColor: settingsStore.detectorName ? Color(accent).alpha(0.04).hexa() : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Badge fontSize="small" sx={{ color: accent, ml: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>Detector Name</Typography>
                            <Typography variant="caption" color="text.secondary">Name of this face detection system for identification</Typography>
                        </Box>
                        <TextField
                            value={settingsStore.detectorName}
                            onChange={(event) => settingsStore.setDetectorName(event.target.value)}
                            placeholder="Enter name"
                            size="small"
                            sx={{ width: 200, ...inputSx(accent) }}
                        />
                    </Box>

                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                        Timing & Thresholds
                    </Typography>

                    <SliderRow
                        accentColor={accent}
                        label="Max Face Capture Duration"
                        value={`${settingsStore.maxFaceCaptureDurationMs}ms`}
                        icon={<Timer fontSize="small" sx={{ color: accent }} />}
                        caption="How long to capture faces before sending the highest quality image"
                        sliderProps={{
                            min: 500, max: 5000, step: 100,
                            value: settingsStore.maxFaceCaptureDurationMs,
                            onChange: (_, v) => settingsStore.setMaxFaceCaptureDurationMs(v as number),
                            marks: [{ value: 500, label: '0.5s' }, { value: 1000, label: '1s' }, { value: 2000, label: '2s' }, { value: 3000, label: '3s' }, { value: 5000, label: '5s' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Face Debounce Duration"
                        value={`${settingsStore.faceDebouceDurationMs}ms`}
                        icon={<AvTimer fontSize="small" sx={{ color: accent }} />}
                        caption="How long to wait before allowing the same face to be processed again"
                        sliderProps={{
                            min: 1000, max: 10000, step: 500,
                            value: settingsStore.faceDebouceDurationMs,
                            onChange: (_, v) => settingsStore.setFaceDebouceDurationMs(v as number),
                            marks: [{ value: 1000, label: '1s' }, { value: 3000, label: '3s' }, { value: 5000, label: '5s' }, { value: 7000, label: '7s' }, { value: 10000, label: '10s' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Event Theme Display Duration"
                        value={`${settingsStore.eventThemeDisplayDurationMs}ms`}
                        icon={<Palette fontSize="small" sx={{ color: accent }} />}
                        caption="How long event theme overlays are displayed before automatically hiding"
                        sliderProps={{
                            min: 2000, max: 10000, step: 500,
                            value: settingsStore.eventThemeDisplayDurationMs,
                            onChange: (_, v) => settingsStore.setEventThemeDisplayDurationMs(v as number),
                            marks: [{ value: 2000, label: '2s' }, { value: 4000, label: '4s' }, { value: 6000, label: '6s' }, { value: 8000, label: '8s' }, { value: 10000, label: '10s' }],
                        }}
                    />

                    <Typography variant="overline" color="text.secondary" sx={{ pt: 1, letterSpacing: 1 }}>
                        Quality & Size
                    </Typography>

                    <SliderRow
                        accentColor={accent}
                        label="Face Ideal Quality"
                        value={settingsStore.faceIdealQuality.toFixed(2)}
                        icon={<Stars fontSize="small" sx={{ color: accent }} />}
                        caption="The ideal quality score for a face image to be sent immediately, bypassing the max capture duration"
                        sliderProps={{
                            min: 0.4, max: 1.0, step: 0.01,
                            value: settingsStore.faceIdealQuality,
                            onChange: (_, v) => settingsStore.setFaceIdealQuality(v as number),
                            marks: [{ value: 0.4, label: '0.4' }, { value: 0.5, label: '0.5' }, { value: 0.65, label: '0.65' }, { value: 0.8, label: '0.8' }, { value: 1.0, label: '1.0' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Min Face Size Percent"
                        value={`${settingsStore.minFaceSizePercent.toFixed(0)}%`}
                        icon={<PhotoSizeSelectSmall fontSize="small" sx={{ color: accent }} />}
                        caption='Minimum face size as a percentage of the native image resolution. Faces smaller than this will show "Move Closer"'
                        sliderProps={{
                            min: 5, max: 100, step: 1,
                            value: settingsStore.minFaceSizePercent,
                            onChange: (_, v) => settingsStore.setMinFaceSizePercent(v as number),
                            marks: [{ value: 5, label: '5%' }, { value: 25, label: '25%' }, { value: 50, label: '50%' }, { value: 75, label: '75%' }, { value: 100, label: '100%' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Min Face Quality"
                        value={settingsStore.minFaceQuality.toFixed(2)}
                        icon={<ThumbUpAlt fontSize="small" sx={{ color: accent }} />}
                        caption="Minimum face quality score required for processing. Faces below this quality will be ignored."
                        sliderProps={{
                            min: 0.0, max: 1.0, step: 0.01,
                            value: settingsStore.minFaceQuality,
                            onChange: (_, v) => settingsStore.setMinFaceQuality(v as number),
                            marks: [{ value: 0.0, label: '0.0' }, { value: 0.2, label: '0.2' }, { value: 0.4, label: '0.4' }, { value: 0.6, label: '0.6' }, { value: 0.8, label: '0.8' }, { value: 1.0, label: '1.0' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Min Sharpness (Laplacian)"
                        value={settingsStore.minLaplacian === 0 ? 'Off' : settingsStore.minLaplacian.toFixed(0)}
                        icon={<ThumbUpAlt fontSize="small" sx={{ color: accent }} />}
                        caption="Minimum Laplacian-variance sharpness required. Faces below this are rejected as too blurry. Set to 0 to disable."
                        sliderProps={{
                            min: 0, max: 6000, step: 50,
                            value: settingsStore.minLaplacian,
                            onChange: (_, v) => settingsStore.setMinLaplacian(v as number),
                            marks: [{ value: 0, label: 'Off' }, { value: 2000, label: '2k' }, { value: 4000, label: '4k' }, { value: 6000, label: '6k' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Min Frontality"
                        value={settingsStore.minFrontality.toFixed(2)}
                        icon={<CenterFocusStrong fontSize="small" sx={{ color: accent }} />}
                        caption="Minimum frontality score required. Faces not looking directly at the camera will be skipped."
                        sliderProps={{
                            min: 0.0, max: 1.0, step: 0.01,
                            value: settingsStore.minFrontality,
                            onChange: (_, v) => settingsStore.setMinFrontality(v as number),
                            marks: [{ value: 0.0, label: '0.0' }, { value: 0.2, label: '0.2' }, { value: 0.4, label: '0.4' }, { value: 0.6, label: '0.6' }, { value: 0.8, label: '0.8' }, { value: 1.0, label: '1.0' }],
                        }}
                    />

                    <SliderRow
                        accentColor={accent}
                        label="Face Expansion Factor"
                        value={`${settingsStore.faceExpansionFactor.toFixed(1)}x`}
                        icon={<ZoomOutMap fontSize="small" sx={{ color: accent }} />}
                        caption="How much to expand the captured face area. Higher values include more context around the face."
                        sliderProps={{
                            min: 1.0, max: 5.0, step: 0.1,
                            value: settingsStore.faceExpansionFactor,
                            onChange: (_, v) => settingsStore.setFaceExpansionFactor(v as number),
                            marks: [{ value: 1.0, label: '1x' }, { value: 2.0, label: '2x' }, { value: 3.0, label: '3x' }, { value: 4.0, label: '4x' }, { value: 5.0, label: '5x' }],
                        }}
                    />
                </Stack>
            </TabPanel>

            {/* ─── Sharing Tab ─── */}
            <TabPanel value={tab} index={2}>
                <Stack spacing={2.5}>
                    <Box
                        sx={{
                            mx: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: Color(accent).alpha(0.3).hexa(),
                            backgroundColor: Color(accent).alpha(0.04).hexa(),
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                        }}
                    >
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                Share your current configuration with others by generating a URL with compressed settings.
                            </Typography>

                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    onClick={handleGenerateShareableUrl}
                                    startIcon={copiedUrl ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                    color={copiedUrl ? 'success' : 'primary'}
                                    variant="contained"
                                    sx={btnContainedSx}
                                >
                                    {copiedUrl ? 'Copied!' : 'Copy Shareable URL'}
                                </Button>

                                <Button
                                    onClick={handleResetToDefaults}
                                    startIcon={<Refresh fontSize="small" />}
                                    variant="outlined"
                                    color="error"
                                    sx={{ ...btnOutlinedSx, borderColor: Color(muiTheme.palette.error.main).alpha(0.3).hexa() }}
                                >
                                    Reset to Defaults
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>

                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                        URL Parameters
                    </Typography>

                    <Box
                        sx={{
                            mx: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: Color(accent).alpha(0.3).hexa(),
                            backgroundColor: Color(accent).alpha(0.04).hexa(),
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: Color(accent).alpha(0.4).hexa(), backgroundColor: Color(accent).alpha(0.03).hexa() },
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Box>
                                <Box component="pre" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1.5, fontSize: '0.8rem', overflowX: 'auto', fontFamily: 'monospace', mb: 0.5 }}>
                                    ?c=compressed_config_data
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    The <code>c</code> parameter contains compressed configuration data. When present, it overrides saved settings and is then removed from the URL.
                                </Typography>
                            </Box>

                            <Box>
                                <Box component="pre" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1.5, fontSize: '0.8rem', overflowX: 'auto', fontFamily: 'monospace', mb: 0.5 }}>
                                    ?c=
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    An empty <code>c</code> parameter resets all settings to defaults and is removed from the URL.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            </TabPanel>
        </Stack>
    );
});

export default GeneralSettings;
